import { BARCODE_EAN13 } from 'innovatint_base/common/BarcodeUtils';
import PropTypes from 'prop-types';
import React from 'react';
import { Image, Rect, Text } from 'react-konva';
import { dragBoundItem, gridSize } from '../../containers/Utils';
import { isMultiBarcodeItem } from '../../mylib/AssignVariables';
import { renderBarcodeCanvas } from '../../mylib/RenderBarcodes';
import AddTransformer from './Transformer';

const DUMMY_BARCODE_CODE = {
  ean13: '978020137962',
  ean8: '9031101',
  code39: 'ABC-1234',
  ean14: '',
  upca: '72527273070',
  isbn: '9781234567897',
  issn: '9771234567898',
  pzn: '',
  itf14: '',
  code128: 'ABC-abc-1234',
  'gs1-128': '(01)12345678901231',
  'gs-128': '(01)12345678901231',
  qrcode: 'This is a QR Code',
  datamatrix: 'This is a Data Matrix',
  databarstacked: '(01)2401234567890',
  databarexpandedstacked: '(01)12345678901231(3202)123456(15)120202',
};

const DUMMY_MULTI_BARCODE_CODE = {
  ean13: '2010000000113\n2020000000181\n2030000000098\n2040000000301',
};

class Barcode extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      gsucc: true,
      barcodecanvas: '',
      multiBarcode: false,
      svg: null,
      options: {
        bcid: BARCODE_EAN13, // Barcode type
        text: DUMMY_BARCODE_CODE.ean13, // Text to encode 978020137962
        scale: 3, // 3x scaling factor
        height: 10, // Bar height, in millimeters
        includetext: false, // Show human-readable text
        textxalign: 'center', // Always good to set this

        dontlint: true,
      },
    };
    this.shapeRef = React.createRef();
    this.trRef = React.createRef();
  }

  //Set the correct state based on Store's props
  static getDerivedStateFromProps(nextProps, prevState) {
    const itm_type =
      nextProps.item.type === 'gs-128' ? 'gs1-128' : nextProps.item.type;
    const nextMultiBarcode = Boolean(
      nextProps.varInfo?.multiBarcode || isMultiBarcodeItem(nextProps.item)
    );
    const dummyText = nextMultiBarcode
      ? DUMMY_MULTI_BARCODE_CODE[itm_type] || DUMMY_BARCODE_CODE[itm_type]
      : DUMMY_BARCODE_CODE[itm_type];
    const nextText = nextProps.useDummyData
      ? dummyText
      : nextProps.item.text;
    const nextSvg = nextProps.useDummyData ? null : nextProps.item.svg;
    if (
      prevState.options.bcid !== itm_type ||
      prevState.options.text !== nextText ||
      prevState.multiBarcode !== nextMultiBarcode ||
      prevState.svg !== nextSvg
    ) {
      return {
        multiBarcode: nextMultiBarcode,
        svg: nextSvg,
        options: {
          ...prevState.options,
          bcid: itm_type,
          text: nextText,
        },
      };
    }
    return null;
  }

  componentDidMount() {
    this.renderBarcodeImage();
  }

  componentWillUnmount() {
    this.svgImage = null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.options.text !== this.state.options.text ||
      prevState.options.bcid !== this.state.options.bcid ||
      prevState.multiBarcode !== this.state.multiBarcode ||
      prevState.svg !== this.state.svg ||
      (!this.state.svg &&
        this.state.multiBarcode &&
        (prevProps.item.width !== this.props.item.width ||
          prevProps.item.height !== this.props.item.height ||
          prevProps.item.showText !== this.props.item.showText))
    ) {
      this.renderBarcodeImage();
    }
  }

  renderBarcodeImage = () => {
    if (this.state.svg) {
      const image = new window.Image();
      this.svgImage = image;
      image.onload = () => {
        if (this.svgImage === image) {
          this.setState({ barcodecanvas: image, gsucc: true });
        }
      };
      image.onerror = () => {
        if (this.svgImage === image) {
          this.setState({ gsucc: false, barcodecanvas: '' });
        }
      };
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        this.state.svg
      )}`;
      return;
    }

    this.svgImage = null;
    try {
      if (this.state.options.text) {
        const barcodecanvas = renderBarcodeCanvas(
          {
            ...this.state.options,
            includetext: false,
          },
          this.state.multiBarcode,
          this.props.item.width
            ? this.props.item.height / this.props.item.width
            : 0,
          this.state.multiBarcode && this.props.item.showText
        );
        this.setState({ barcodecanvas, gsucc: true });
      } else {
        this.setState({ gsucc: false, barcodecanvas: '' });
      }
    } catch (e) {
      console.log(e);
      this.setState({ gsucc: false, barcodecanvas: '' });
    }
  };

  dragBound = (pos, rotation) => {
    const { x, y } = dragBoundItem(
      pos,
      this.props.layout,
      this.props.item,
      rotation
    );
    return { x, y };
  };

  render() {
    const { item, move, selectedItemId, readonly, selectItem, updateItem } =
      this.props;
    const name = 'barcode' + item.id;
    const isSelected = item.id === this.props.selectedItemId;
    return (
      <React.Fragment>
        {isSelected && !readonly && (
          <Rect
            x={item.x - 2}
            y={item.y - 2}
            width={item.width + 4}
            height={item.height + 4}
            stroke={'#99EEFF88'}
            strokeWidth={2}
            shadowBlur={10}
            shadowOffset={{ x: 5, y: 5 }}
            shadowOpacity={0.5}
            rotation={item.rotation}
          />
        )}
        {this.state.gsucc ? (
          <>
            <Image
              name={name}
              ref={this.shapeRef}
              x={item.x}
              y={item.y}
              image={this.state.barcodecanvas}
              width={item.width}
              height={item.height}
              rotation={item.rotation}
              draggable={move && selectedItemId === item.id}
              //fill={this.state.isDragging ? 'green' : 'black'}
              dragBoundFunc={(pos) =>
                this.dragBound(pos, Number(item.rotation))
              }
              onClick={readonly ? null : () => selectItem(item.id)}
              onTransformEnd={(e) => {
                // transformer is changing scale
                const node = this.shapeRef.current;
                const scaleX = node.scaleX();
                const scaleY = node.scaleY();
                // we will reset it back
                node.scaleX(1);
                node.scaleY(1);
                updateItem({
                  ...item,
                  x: node.x(),
                  y: node.y(),
                  width: Math.min(Math.max(node.width() * scaleX, 25), 150),
                  height: this.props.varInfo?.multiBarcode
                    ? Math.max(node.height() * scaleY, 25)
                    : Math.min(
                        Math.max(node.height() * scaleY, 25),
                        150
                      ),
                });
              }}
              onDragEnd={(e) => {
                const x = Math.round(e.target.x() / gridSize) * gridSize;
                const y = Math.round(e.target.y() / gridSize) * gridSize;
                this.shapeRef.current.position({
                  x,
                  y,
                });
                updateItem({
                  ...item,
                  x,
                  y,
                });
              }}
            />
            {item.showText &&
              !(this.state.multiBarcode && this.props.useDummyData) && (
                <Text
                  name={name}
                  x={item.x}
                  y={item.y + item.height}
                  width={item.width}
                  height={item.height}
                  text={item.text || this.state.options.text}
                />
              )}
          </>
        ) : (
          // Now the data is displayed as red when impossible to render as barcode. Maybe something else would work better...
          <Text
            name={name}
            x={item.x}
            y={item.y}
            width={item.width}
            height={item.height}
            text={item.text}
            fill={'red'}
          />
        )}

        {readonly || !move ? null : (
          <AddTransformer
            ref={this.trRef}
            selectedShapeName={isSelected ? name : null}
          />
        )}
      </React.Fragment>
    );
  }
}
Barcode.propTypes = {
  readonly: PropTypes.bool,
  item: PropTypes.object,
  selectedItemId: PropTypes.number,
  selectItem: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  layout: PropTypes.object,
  standAlone: PropTypes.bool,
  useDummyData: PropTypes.bool,
  varInfo: PropTypes.object,
  move: PropTypes.bool,
};

Barcode.defaultProps = {
  readonly: false,
  useDummyData: true,
  selectItem: () => {},
  updateItem: () => {},
};

export default Barcode;
